import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';

const TOTAL_YEARS = 90;
const WEEKS_PER_YEAR = 52;
const TOTAL_WEEKS = TOTAL_YEARS * WEEKS_PER_YEAR;

export const calculateWeeksLived = (birthDate) => {
  if (!birthDate) return 0;
  const now = new Date();
  const birth = new Date(birthDate);
  const diffTime = now - birth;
  const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));
  return Math.max(0, Math.min(diffWeeks, TOTAL_WEEKS));
};

export const calculateAge = (birthDate) => {
  if (!birthDate) return 0;
  const now = new Date();
  const birth = new Date(birthDate);
  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) {
    age--;
  }
  return Math.max(0, age);
};

// Calculate age they will be on their NEXT birthday
export const calculateNextBirthdayAge = (birthDate) => {
  if (!birthDate) return 0;
  const now = new Date();
  const birth = new Date(birthDate);
  
  // Get current age
  let currentAge = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  const dayDiff = now.getDate() - birth.getDate();
  
  // If birthday hasn't happened yet this year, current age is one less
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    currentAge--;
  }
  
  // Next birthday age is always current age + 1
  return Math.max(1, currentAge + 1);
};

export const LifeInWeeksGrid = ({ 
  birthDate, 
  compact = false,
  showLabels = true,
  filledColor = '#1A1A1A',
  emptyColor = '#F0EEEA',
  className 
}) => {
  const weeksLived = useMemo(() => calculateWeeksLived(birthDate), [birthDate]);
  
  const gridData = useMemo(() => {
    const data = [];
    for (let year = 0; year < TOTAL_YEARS; year++) {
      const yearData = [];
      for (let week = 0; week < WEEKS_PER_YEAR; week++) {
        const weekIndex = year * WEEKS_PER_YEAR + week;
        yearData.push({
          index: weekIndex,
          lived: weekIndex < weeksLived,
          year,
          week
        });
      }
      data.push(yearData);
    }
    return data;
  }, [weeksLived]);

  // V2 spec: ~1.35mm x 1.27mm per week with 0.3mm gap
  // For compact (printable): smaller cells for 88mm x 114mm area
  const cellSize = compact ? 'w-[1.35mm] h-[1.27mm]' : 'w-[6px] h-[6px] sm:w-[8px] sm:h-[8px]';
  const gap = compact ? 'gap-[0.3mm]' : 'gap-[2px]';

  return (
    <div className={cn('flex flex-col', className)} data-testid="life-grid-container">
      {showLabels && !compact && (
        <div className="flex mb-2">
          <span className="w-8 shrink-0" />
          <div className="flex-1 flex justify-between">
            <span className="text-[10px] uppercase tracking-widest text-[#737373] font-medium">Week 1</span>
            <span className="text-[10px] uppercase tracking-widest text-[#737373] font-medium">Week 52</span>
          </div>
        </div>
      )}
      <div className={cn('flex flex-col', gap)}>
        {gridData.map((yearRow, yearIndex) => (
          <div key={yearIndex} className="flex items-center">
            {showLabels && !compact && (
              <span className="text-[9px] text-[#737373] w-8 shrink-0 text-right pr-2 font-medium">
                {yearIndex % 10 === 0 ? yearIndex : ''}
              </span>
            )}
            <div className={cn('flex', gap)}>
              {yearRow.map((cell) => (
                <div
                  key={cell.index}
                  data-testid={`life-cell-${cell.index}`}
                  className={cn(
                    cellSize,
                    'rounded-[1px] life-cell'
                  )}
                  style={{
                    backgroundColor: cell.lived ? filledColor : emptyColor
                  }}
                  title={`Year ${cell.year + 1}, Week ${cell.week + 1}`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      {showLabels && !compact && (
        <div className="flex mt-2">
          <span className="w-8 shrink-0" />
          <div className="flex-1 flex justify-between">
            <span className="text-[10px] uppercase tracking-widest text-[#737373] font-medium">Birth</span>
            <span className="text-[10px] uppercase tracking-widest text-[#737373] font-medium">90 Years</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LifeInWeeksGrid;
